/**
 * /enroll/:token — Public enrollment page reached from an email link.
 *
 * No auth required. Shows the package summary, the coach's note (if any),
 * and a single "Enroll & pay" button that creates a Stripe Checkout
 * session and redirects to the hosted checkout page.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, AlertTriangle, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/enroll/${token}`);
                setData(r.data);
            } catch (e) {
                setError(e?.response?.data?.detail || "Link not found");
            } finally {
                setBusy(false);
            }
        })();
    }, [token]);

    const start = async () => {
        setStarting(true);
        try {
            const r = await axios.post(`${API}/enroll/${token}/checkout`, {
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error("Couldn't start checkout", { description: e?.response?.data?.detail });
            setStarting(false);
        }
    };

    if (busy) {
        return (
            <div data-testid="enroll-page" className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-brand-red animate-spin" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div data-testid="enroll-page" className="min-h-screen bg-brand-obsidian text-white flex items-center justify-center px-4">
                <div className="max-w-md text-center border border-zinc-800 bg-zinc-950 rounded-2xl p-8">
                    <AlertTriangle className="w-7 h-7 text-brand-red mx-auto mb-3" />
                    <h2 className="font-display text-2xl tracking-wider">Link unavailable</h2>
                    <p className="text-sm text-zinc-300 mt-2">{error || "We couldn't find this enrollment link."}</p>
                    <button type="button" onClick={() => navigate("/")} className="btn-tactical text-xs mt-5">Go to home</button>
                </div>
            </div>
        );
    }

    if (data.status === "expired" || data.status === "redeemed") {
        return (
            <div data-testid="enroll-page" className="min-h-screen bg-brand-obsidian text-white flex items-center justify-center px-4">
                <div className="max-w-md text-center border border-zinc-800 bg-zinc-950 rounded-2xl p-8">
                    <AlertTriangle className="w-7 h-7 text-amber-400 mx-auto mb-3" />
                    <h2 className="font-display text-2xl tracking-wider">{data.status === "expired" ? "Link expired" : "Already enrolled"}</h2>
                    <p className="text-sm text-zinc-300 mt-2">
                        {data.status === "expired"
                            ? "This link has timed out. Reply to the email and your coach will send a fresh one."
                            : "This link has already been used. Check your inbox for the receipt."}
                    </p>
                </div>
            </div>
        );
    }

    const isCardSave = data.kind === "card_save";
    const amountStr = isCardSave ? "$0.00" : `$${(data.amount_cents / 100).toFixed(2)}`;
    return (
        <div data-testid="enroll-page" className="min-h-screen bg-brand-obsidian text-white flex items-center justify-center px-4 py-12">
            <div className="max-w-lg w-full border border-zinc-800 bg-zinc-950 rounded-2xl overflow-hidden">
                <div className="bg-brand-red h-1" />
                <div className="p-7">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-1">{isCardSave ? "Card on file" : "Enrollment"}</div>
                    <h1 className="font-display text-3xl tracking-wider">{isCardSave ? "Save your card." : "Lock in your spot."}</h1>
                    <p className="text-sm text-zinc-300 mt-2">
                        Hey {(data.user_name || "").split(" ")[0] || "there"} — {isCardSave ? "$0 charged today. We just keep the card on file so check-ins, drop-ins, and packages are 1-tap from now on. " : ""}Card form is hosted securely by Stripe.
                    </p>

                    <div className="mt-6 border border-zinc-800 rounded-xl p-4 bg-black/30">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">{isCardSave ? "Today's charge" : "Package"}</div>
                        <div className="text-lg text-white mt-1">{data.item_name || (isCardSave ? "Save card on file" : "—")}</div>
                        <div className="text-3xl font-display tracking-wider mt-2 text-brand-red">{amountStr}{data.plan_id ? " /mo" : ""}</div>
                    </div>

                    {data.custom_note && (
                        <div className="mt-4 border-l-2 border-brand-red pl-3 text-sm text-zinc-300 whitespace-pre-wrap">{data.custom_note}</div>
                    )}

                    <button
                        type="button"
                        data-testid="enroll-pay"
                        onClick={start}
                        disabled={starting}
                        className="mt-6 w-full bg-brand-red text-white font-semibold tracking-[0.06em] py-3.5 rounded-lg hover:bg-brand-red/90 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                        {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                        {isCardSave ? "Add card on file" : "Enroll & pay"}
                    </button>
                    <div className="mt-3 text-[10px] tracking-[0.25em] uppercase text-zinc-400 text-center inline-flex items-center justify-center gap-1.5 w-full">
                        <Lock className="w-3 h-3" /> Stripe · PCI-DSS Level 1
                    </div>
                </div>
            </div>
        </div>
    );
}


export function EnrollSuccess() {
    return (
        <div className="min-h-screen bg-brand-obsidian text-white flex items-center justify-center px-4">
            <div className="max-w-md text-center border border-zinc-800 bg-zinc-950 rounded-2xl p-8">
                <CheckCircle className="w-9 h-9 text-emerald-400 mx-auto mb-3" />
                <h2 className="font-display text-3xl tracking-wider">You're in.</h2>
                <p className="text-sm text-zinc-300 mt-3">
                    Payment received — receipt is on its way to your inbox. Sign in to your dashboard to book your first session.
                </p>
                <a href="/dashboard" className="btn-tactical text-xs mt-6 inline-flex">Go to dashboard</a>
            </div>
        </div>
    );
}
